import { Button, Descriptions, Divider, Drawer, InputNumber, Space, Table } from "antd";
import Link from "antd/es/typography/Link";
import { addMinutes, format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { api } from "../../config";
import {
  TicketViewModel,
} from "../../interfaces";
import { AuthenticationState } from "../../interfaces/users";
import { formatCurrency } from "../../utils/helpers/ticketHelpers";

export interface SuggestionDraftEvent {
  id: number;
  kind: "draft";
  start: string | Date;
  end: string | Date;
  fieldReportId: number;
  assemblerId: number;
  assembler?: {
    id: number;
    fullName: string;
    seniorityId: number;
  };
}

interface SuggestionEventDrawerProps {
  open: boolean;
  draftEvent?: SuggestionDraftEvent | null;
  onClose: () => void;
  onRemove: (draftEvent: SuggestionDraftEvent) => void;
  onSave: (draftEvent: SuggestionDraftEvent, nextEnd: Date) => Promise<void>;
}

const getAdditionsTotals = (ticket?: TicketViewModel) => {
  return (ticket?.PaymentAdditions ?? []).reduce(
    (totals, addition) => {
      const value = Number(addition.value ?? 0);
      const status = addition.status as unknown as string;
      if (status === "APPROVED") {
        totals.approved += value;
      }
      if (status === "PENDING_APPROVEMENT") {
        totals.pending += value;
      }
      return totals;
    },
    { approved: 0, pending: 0 }
  );
};

const SuggestionEventDrawer = ({
  open,
  draftEvent,
  onClose,
  onRemove,
  onSave,
}: SuggestionEventDrawerProps) => {
  const { token } = useSelector((state: AuthenticationState) => state);
  const [durationHours, setDurationHours] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  const { data: ticket } = useQuery<TicketViewModel>(
    ["suggestion-ticket", draftEvent?.fieldReportId],
    () => api(token).get(`ticket/assemble/${draftEvent?.fieldReportId}`),
    {
      enabled: open && Boolean(draftEvent?.fieldReportId),
    }
  );

  useEffect(() => {
    if (!draftEvent) {
      setDurationHours(0);
      return;
    }

    const start = new Date(draftEvent.start);
    const end = new Date(draftEvent.end);
    const minutes = Math.max((end.getTime() - start.getTime()) / 60000, 0);
    setDurationHours(minutes / 60);
  }, [draftEvent]);

  const totals = useMemo(() => {
    const productsTotal = (ticket?.products ?? []).reduce(
      (total, product: any) => total + Number(product?.value ?? 0),
      0
    );
    const additionsTotals = getAdditionsTotals(ticket);

    return {
      productsTotal,
      approvedAdditionsTotal: additionsTotals.approved,
      pendingAdditionsTotal: additionsTotals.pending,
      ticketTotal:
        productsTotal + additionsTotals.approved + additionsTotals.pending,
    };
  }, [ticket]);

  const address = ticket?.client?.address;

  return (
    <Drawer
      title="Confirmar Sugestão"
      placement="right"
      open={open}
      onClose={onClose}
      size="large"
    >
      {draftEvent && ticket ? (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Título">
              <Link
                href={`/montagem?id=${draftEvent.fieldReportId}`}
                target="_blank"
                underline
              >
                {ticket.title}
              </Link>
            </Descriptions.Item>
            <Descriptions.Item label="Montador">
              {draftEvent.assembler?.fullName ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Início">
              {format(new Date(draftEvent.start), "dd/MM/yyyy HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Fim previsto">
              {format(
                addMinutes(
                  new Date(draftEvent.start),
                  Math.round(durationHours * 60)
                ),
                "dd/MM/yyyy HH:mm"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Duração (horas)">
              <InputNumber
                min={0.25}
                step={0.25}
                precision={2}
                value={durationHours}
                onChange={(value) =>
                  setDurationHours(Number(value ?? 0.25))
                }
              />
            </Descriptions.Item>
            <Descriptions.Item label="Total dos produtos">
              {formatCurrency(totals.productsTotal)}
            </Descriptions.Item>
            <Descriptions.Item label="Total dos adicionais aprovados">
              {formatCurrency(totals.approvedAdditionsTotal)}
            </Descriptions.Item>
            <Descriptions.Item label="Total dos adicionais não aprovados">
              {formatCurrency(totals.pendingAdditionsTotal)}
            </Descriptions.Item>
            <Descriptions.Item label="Total do ticket">
              {formatCurrency(totals.ticketTotal)}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Descriptions title="Cliente" column={1} size="small" bordered>
            <Descriptions.Item label="Nome">
              {ticket.client?.name ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="CPF/CNPJ">
              {ticket.client?.taxId ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Telefone">
              {ticket.client?.phoneNumber ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Telefone alternativo">
              {ticket.client?.alternativePhoneNumber ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Endereço">
              {[address?.street, address?.add]
                .filter(Boolean)
                .join(" ") || "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Bairro">
              {address?.neighbor ?? "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="Cidade/UF">
              {[address?.city, address?.state].filter(Boolean).join("/") ||
                "Não informado"}
            </Descriptions.Item>
            <Descriptions.Item label="CEP">
              {address?.zipCode ?? "Não informado"}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Table
            dataSource={ticket.products}
            rowKey={(product: any) => product.fieldReportProductId ?? product.id}
            pagination={false}
            size="small"
          >
            <Table.Column title="Produto" dataIndex="description" key="description" />
            <Table.Column title="Quantidade" dataIndex="quantity" key="quantity" />
            <Table.Column
              title="Valor"
              dataIndex="value"
              key="value"
              render={(value: number) => formatCurrency(Number(value ?? 0))}
            />
            <Table.Column
              title="Tempo de Montagem"
              dataIndex="assemblyTime"
              key="assemblyTime"
              render={(value: number) => `${value ?? 0}h`}
            />
          </Table>

          <Divider />

          <Space>
            <Button danger onClick={() => onRemove(draftEvent)}>
              Remover Sugestão
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={async () => {
                try {
                  setSaving(true);
                  await onSave(
                    draftEvent,
                    addMinutes(
                      new Date(draftEvent.start),
                      Math.round(durationHours * 60)
                    )
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              Salvar Evento
            </Button>
          </Space>
        </>
      ) : null}
    </Drawer>
  );
};

export default SuggestionEventDrawer;
